/**
 * Toạ độ cho đường kẻ vẽ bằng SVG thuần của trang Tổng quan.
 *
 * Tách khỏi component để test được bằng Vitest ở môi trường `node`
 * (xem apps/web/vitest.config.ts). Vẽ bằng SVG thuần, không thư viện biểu đồ
 * (quyết định đã chốt số 5 trong CLAUDE.md).
 *
 * Trước 29/10/2026 đây là phần tính của khối "Lượt xem toàn hệ thống" và còn
 * `duongVung` (vùng tô), `viTriNhan` (nhãn trục) và `khoaTuanIso`. Khối đó đã
 * bỏ (xem `docs/ui.md` mục 18); phần còn lại dùng cho đường 14 ngày trong khung
 * "Tăng trưởng lượt xem · 7 ngày" — một nét mảnh, không trục, không vùng tô.
 */

export interface KhungBieuDo {
  /** Chiều rộng hệ toạ độ trong `viewBox`. */
  rong: number;
  /** Chiều cao hệ toạ độ trong `viewBox`. */
  cao: number;
  /** Chừa mép trên dưới để đường không chạm viền. */
  dem: number;
}

/**
 * Đệm **trên trục giá trị**: trần của trục cao hơn giá trị lớn nhất 10% biên
 * độ, nên đỉnh cao nhất dừng cách mép trên khung khoảng 1/11 chiều cao vẽ.
 *
 * `dem` một mình không đủ: nét vẽ *không co theo khung*
 * (`vectorEffect="non-scaling-stroke"`), nên ở chuỗi có một đỉnh vượt hẳn phần
 * còn lại, nửa trên của nét đỉnh chạm — có khi vượt — mép trên và bị
 * `overflow` của `<svg>` cắt mất. Đỉnh bị cắt đọc ra một con số thấp hơn thật.
 */
export const DEM_TRAN_TRUC = 0.1;

export interface Diem {
  x: number;
  y: number;
}

/**
 * Chia đều các điểm theo trục ngang, trục dọc tính theo khoảng
 * `[min, max]` của chính dãy số. Dãy phẳng (min = max) thì vẽ ở giữa.
 */
export function toaDo(giaTri: number[], khung: KhungBieuDo): Diem[] {
  if (giaTri.length === 0) return [];

  const { rong, cao, dem } = khung;
  const cuoi = giaTri.length - 1;
  const nhoNhat = Math.min(...giaTri);
  const lonNhat = Math.max(...giaTri);
  const bienDo = lonNhat - nhoNhat;
  const caoVe = cao - dem * 2;
  /* Trần trục, không phải giá trị lớn nhất — xem `DEM_TRAN_TRUC`. */
  const tran = lonNhat + bienDo * DEM_TRAN_TRUC;

  return giaTri.map((v, i) => ({
    x: cuoi === 0 ? rong / 2 : (i / cuoi) * rong,
    y: bienDo === 0 ? cao / 2 : dem + (1 - (v - nhoNhat) / (tran - nhoNhat)) * caoVe,
  }));
}

/** Đường gấp khúc nối các điểm, dùng cho `<path d>`. */
export function duongKe(diem: Diem[]): string {
  if (diem.length === 0) return '';
  return diem.map((d, i) => `${i === 0 ? 'M' : 'L'}${d.x.toFixed(2)} ${d.y.toFixed(2)}`).join(' ');
}
